import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronLeft, ChevronRight, Sun, Moon } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { navConfig } from "../../config/navigation";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from '../../contexts/AuthContext';
import ddLogo from '../ui/dd.png';

import MenuItem from "./MenuItem";
import MenuSection from "./MenuSection";
import ThemeToggle from "./ThemeToggle";
import MobileHeader from "./MobileHeader";
import UserProfile from "./UserProfile";

const Sidebar = ({
  role = "admin",
  customNavConfig,
  defaultCollapsed = false,
  allowCollapse = true,
  appName,
  onCollapseChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === "dark";
  const { name, email, isLoading: authLoading, firstName, lastName, role: userRole } = useAuth();
  
  // State
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    image: null,
    plan: "",
    role: "",
  });
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  // Use provided nav config or fall back to default
  const config = customNavConfig || navConfig[role];

  // Effects
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  // Update user info from auth context instead of making API calls
  useEffect(() => {
    if (!authLoading && name && email) {
      setUserInfo({
        name: name,
        email: email,
        image: null, // Could be added to auth context later if needed
        plan: "", // Could be added to auth context later if needed
        role: userRole || role,
      });
    }
  }, [authLoading, name, email, userRole, role]);

  const isMobile = windowWidth < 768;

  // Handlers
  const handleMobileItemClick = useCallback(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  const toggleCollapse = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  }, [isCollapsed, onCollapseChange]);

  // Dynamic width based on collapsed state
  const sidebarWidth = isCollapsed ? "w-16" : "w-64";

  // Collapse/expand toggle button
  const CollapseToggle = () => {
    if (isCollapsed) {
      return (
        <button
                  onClick={toggleCollapse}
        className={`w-full flex justify-center p-2 my-1 hover:bg-brand-logo-purple/10 text-gray-400 hover:text-brand-logo-purple transition-colors`}
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <ChevronRight size={16} />
        </button>
      );
    }

    return (
      <button
        onClick={toggleCollapse}
        className={`p-2 rounded-full ${
          isDarkMode
            ? "text-gray-400 hover:text-brand-logo-purple hover:bg-brand-logo-purple/10"
            : "text-gray-600 hover:text-gray-800"
        } transition-colors`}
        aria-label="Collapse sidebar"
        title="Collapse"
      >
        <ChevronLeft size={18} />
      </button>
    );
  };

  // Mobile menu toggle button
  const MobileMenuButton = () => (
    <button
      onClick={() => setIsMobileMenuOpen(true)}
      className={`fixed top-4 left-4 z-50 p-2 rounded-lg ${
        isDarkMode
          ? "bg-brand-logo-purple text-white shadow-lg shadow-brand-logo-purple/25"
          : "bg-primary text-primary-content"
      } shadow-lg md:hidden`}
      aria-label="Open menu"
    >
      <Menu size={20} />
    </button>
  );

  // Mobile menu overlay
  const MobileMenuOverlay = () => (
    <div
      className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
        isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={() => setIsMobileMenuOpen(false)}
      aria-hidden="true"
    />
  );

  // Mobile menu drawer
  const MobileMenuDrawer = () => (
    <div
              className={`fixed inset-y-0 left-0 w-72 z-50 
        ${isDarkMode ? "bg-sidebar-gradient border-r border-brand-border-logo" : "bg-white"} 
        shadow-xl transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
      `}
    >
      <div className="flex flex-col h-full">
        <MobileHeader 
          appName={config.title || appName || (role === "admin" ? "Dance Digital" : "Studio Portal")}
          isDarkMode={isDarkMode}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        
        <div className="flex-1 overflow-y-auto py-2">
          {config.sections.map((item, index) => (
            item.type === "section" ? (
              <MenuSection
                key={`section-${index}`}
                title={item.title}
                defaultOpen={item.defaultOpen}
                isDarkMode={isDarkMode}
                isCollapsed={false}
                isMobile={true}
                onItemClick={handleMobileItemClick}
              >
                {item.items.map((subItem, subIndex) => (
                  <MenuItem
                    key={`item-${subIndex}`}
                    {...subItem}
                    isActive={location.pathname === subItem.to}
                    isDarkMode={isDarkMode}
                    isCollapsed={false}
                    level={1}
                    isMobile={true}
                    onItemClick={handleMobileItemClick}
                  />
                ))}
              </MenuSection>
            ) : (
              <MenuItem
                key={`main-item-${index}`}
                {...item}
                isActive={location.pathname === item.to}
                isDarkMode={isDarkMode}
                isCollapsed={false}
                isMobile={true}
                onItemClick={handleMobileItemClick}
              />
            )
          ))}
        </div>
        
        <div className={`p-4 border-t ${isDarkMode ? "border-brand-border-logo" : "border-gray-200"}`}>
          <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} isMobile={true} />
          <UserProfile 
            userInfo={userInfo}
            isLoading={authLoading}
            isDarkMode={isDarkMode}
            isMobile={true}
            handleMobileItemClick={handleMobileItemClick}
            navigate={navigate}
            role={role}
          />
        </div>
      </div>
    </div>
  );

  // Desktop sidebar
  const DesktopSidebar = () => (
    <aside
      className={`fixed left-0 top-0 h-screen ${sidebarWidth} transition-all duration-300 ease-in-out z-40
        ${
          isDarkMode
            ? "bg-sidebar-gradient border-r border-brand-border-logo shadow-xl"
            : "bg-base-100 border-r border-base-200"
        }
      `}
      aria-label={role === "admin" ? "Admin navigation" : "User navigation"}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-2">
          <div className="flex items-center gap-3">
            <div className={`${isCollapsed ? "mx-auto" : ""} relative`}>
              <div className="w-9 h-9 relative transition-all duration-300">
                <img 
                  src={ddLogo}
                  alt="Dance Digital Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {!isCollapsed && (
              <h1 className="text-xl font-semibold tracking-tight">
                {config.title || appName || (role === "admin" ? "Dance Digital" : "Studio Portal")}
              </h1>
            )}
          </div>

          {!isCollapsed && allowCollapse && <CollapseToggle />}
        </div>

        {/* Collapse toggle under logo when collapsed */}
        {isCollapsed && allowCollapse && <CollapseToggle />}

        {/* Navigation */}
        <div className={`flex-1 overflow-y-auto py-4 px-2 space-y-1`}>
          {config.sections.map((item, index) => (
            item.type === "section" ? (
              <MenuSection
                key={`section-${index}`}
                title={item.title}
                defaultOpen={item.defaultOpen}
                isDarkMode={isDarkMode}
                isCollapsed={isCollapsed}
                isMobile={false}
                onItemClick={handleMobileItemClick}
              >
                {item.items.map((subItem, subIndex) => (
                  <MenuItem
                    key={`item-${subIndex}`}
                    {...subItem}
                    isActive={location.pathname === subItem.to}
                    isDarkMode={isDarkMode}
                    isCollapsed={isCollapsed}
                    level={1}
                    isMobile={false}
                    onItemClick={handleMobileItemClick}
                  />
                ))}
              </MenuSection>
            ) : (
              <MenuItem
                key={`main-item-${index}`}
                {...item}
                isActive={location.pathname === item.to}
                isDarkMode={isDarkMode}
                isCollapsed={isCollapsed}
                isMobile={false}
                onItemClick={handleMobileItemClick}
              />
            )
          ))}
        </div>

        {/* Bottom controls section */}
        <div className="p-3 space-y-3 border-t border-brand-border-logo">
          {!isCollapsed && (
            <div className="px-1">
              <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
            </div>
          )}

          {/* Dark mode toggle when collapsed */}
          {isCollapsed && (
            <div className="flex flex-col items-center space-y-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full ${
                  isDarkMode
                    ? "bg-brand-logo-purple/20 text-brand-logo-purple border border-brand-logo-purple/30"
                    : "bg-gray-200 text-gray-800"
                }`}
                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                title={isDarkMode ? "Light mode" : "Dark mode"}
              >
                {isDarkMode ? <Sun size={16} /> : <Moon size={20} />}
              </button>
            </div>
          )}

          {/* User profile section */}
          <UserProfile 
            userInfo={userInfo}
            isLoading={authLoading}
            isDarkMode={isDarkMode}
            isMobile={false}
            isCollapsed={isCollapsed}
            handleMobileItemClick={handleMobileItemClick}
            navigate={navigate}
            role={role}
          />
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile Menu Button - Only visible on smaller screens */}
      {isMobile && <MobileMenuButton />}
      
      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay />
      
      {/* Mobile Menu Drawer */}
      <MobileMenuDrawer />
      
      {/* Desktop Sidebar - Hidden on mobile */}
      {!isMobile && <DesktopSidebar />}
    </>
  );
};

export default Sidebar; 